import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { User } from '~/schemas/user'; // Import User type if needed

// Define message type matching schemas/message.ts (adjust if needed)
export enum actorType {
  BOT = 'BOT',
  USER = 'USER',
  AGENT = 'AGENT',
  HUMAN_AGENT = 'HUMAN_AGENT',
}

// Ensure Tokens type matches the required schema, potentially non-optional
interface Tokens {
  model?: string;
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
}

export interface Message {
  _id: string; // Assuming _id is used internally, keep it
  id: string; // From schema
  created_at: string;
  conversation_id: string;
  agent_id: string;
  from_number?: string;
  user_id: string;
  channel_id: string;
  to_number?: string;
  message: string;
  predifined_message?: Record<string, any>;
  actorType: actorType;
  reopen?: boolean;
  tokens: Tokens; // Make tokens non-optional to match schema expectation
  media?: {
    url: string;
    type: string;
    caption?: string;
    fileName?: string;
    file?: File;
    origin?: string;
  };
  user?: User;
  // Keep other fields if necessary
  [key: string]: any;
}

enableMapSet();

interface MessagesState {
  messagesByConversation: Record<string, Message[]>;
  // Removed cursorsByConversation as pagination logic will move to component if needed
  loadingByConversation: Record<string, boolean>;
  sendingByConversation: Record<string, boolean>;
  errorByConversation: Record<string, string | null>;
}

const initialState: MessagesState = {
  messagesByConversation: {},
  loadingByConversation: {},
  sendingByConversation: {},
  errorByConversation: {},
};

// Helper to ensure message has a default tokens object if missing
const ensureTokens = (message: Partial<Message>): Message => {
  return {
    ...message,
    tokens: message.tokens || {},
  } as Message; // Cast as Message, assuming other required fields are present
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setMessagesLoading: (
      state,
      action: PayloadAction<{ conversationId: string; isLoading: boolean }>,
    ) => {
      state.loadingByConversation[action.payload.conversationId] =
        action.payload.isLoading;
      if (action.payload.isLoading) {
        // Clear error when starting to load
        state.errorByConversation[action.payload.conversationId] = null;
      }
    },
    setMessagesError: (
      state,
      action: PayloadAction<{ conversationId: string; error: string | null }>,
    ) => {
      state.errorByConversation[action.payload.conversationId] =
        action.payload.error;
      state.loadingByConversation[action.payload.conversationId] = false; // Ensure loading is false on error
    },
    addFetchedMessages: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messages: Partial<Message>[];
      }>,
    ) => {
      const { conversationId, messages } = action.payload;
      const existingMessages =
        state.messagesByConversation[conversationId] || [];
      const uniqueNewMessages = messages
        .map(ensureTokens) // Ensure tokens object exists
        .filter(
          (nm) =>
            !existingMessages.some(
              (em) => em._id === nm._id || em.id === nm.id,
            ),
        );
      state.messagesByConversation[conversationId] = [
        ...existingMessages,
        ...uniqueNewMessages,
      ].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      state.loadingByConversation[conversationId] = false; // Mark loading as false after adding
    },
    addMessage: (
      state,
      action: PayloadAction<{
        conversationId: string;
        message: Partial<Message>;
      }>,
    ) => {
      const { conversationId, message } = action.payload;
      const ensuredMessage = ensureTokens(message); // Ensure tokens object exists
      const existingMessages =
        state.messagesByConversation[conversationId] || [];
      // Avoid adding duplicates if already exists

      if (!existingMessages.some((m) => m._id === ensuredMessage._id)) {
        state.messagesByConversation[conversationId] = [
          ...existingMessages,
          ensuredMessage,
        ].sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
      }
      // Ensure sending state is false if we receive the message we might have sent
      state.sendingByConversation[conversationId] = false;
    },
    removeMessage: (
      state,
      action: PayloadAction<{ conversationId: string; messageId: string }>,
    ) => {
      const { conversationId, messageId } = action.payload;
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId] =
          state.messagesByConversation[conversationId].filter(
            (m) => m._id !== messageId && m.id !== messageId, // Check both id and _id
          );
      }
    },
    setSendingMessage: (
      state,
      action: PayloadAction<{ conversationId: string; isSending: boolean }>,
    ) => {
      state.sendingByConversation[action.payload.conversationId] =
        action.payload.isSending;
      if (action.payload.isSending) {
        // Clear error when starting to send
        state.errorByConversation[action.payload.conversationId] = null;
      }
    },
    setSendingError: (
      state,
      action: PayloadAction<{ conversationId: string; error: string | null }>,
    ) => {
      state.errorByConversation[action.payload.conversationId] =
        action.payload.error;
      state.sendingByConversation[action.payload.conversationId] = false; // Ensure sending is false on error
    },
    clearConversationMessages: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      delete state.messagesByConversation[conversationId];
      delete state.loadingByConversation[conversationId];
      delete state.sendingByConversation[conversationId];
      delete state.errorByConversation[conversationId];
    },
  },
  // Removed extraReducers as async logic is moved to the component
});

export const {
  setMessagesLoading,
  setMessagesError,
  addFetchedMessages,
  addMessage,
  removeMessage,
  setSendingMessage,
  setSendingError,
  clearConversationMessages,
} = messagesSlice.actions;

// Define RootState type for selectors
// Ensure this matches your actual combined root state structure
export interface AppRootState {
  messages: MessagesState;
  // include other state slices here
  activities: any; // Replace 'any' with actual ActivitiesState type if available
  activityRecords: any; // Replace 'any' with actual ActivityRecordsState type if available
}

// Input selector for the messages map
const selectMessagesMap = (state: AppRootState) =>
  state.messages.messagesByConversation;

// Input selector for the conversationId (passed as the second argument)
const selectConversationId = (
  _state: AppRootState,
  conversationId: string | undefined,
) => conversationId;

// Stable empty array reference
const EMPTY_MESSAGES: Message[] = [];

// Intermediate selector for the specific conversation's messages or empty array
export const selectMessagesByConversationId = createSelector(
  [selectMessagesMap, selectConversationId],
  (messagesMap, conversationId): Message[] => {
    if (!conversationId) {
      return EMPTY_MESSAGES; // Return stable empty array
    }
    // Returns the array reference from the map or the stable empty array reference
    return messagesMap[conversationId] || EMPTY_MESSAGES;
  },
);

export const selectMessagesLoading = (
  state: AppRootState,
  conversationId: string | undefined,
): boolean => {
  if (!conversationId) return false;
  return state.messages.loadingByConversation[conversationId] || false;
};

export const selectIsSendingMessage = (
  state: AppRootState,
  conversationId: string | undefined,
): boolean => {
  if (!conversationId) return false;
  return state.messages.sendingByConversation[conversationId] || false;
};

export const selectMessagesError = (
  state: AppRootState,
  conversationId: string | undefined,
): string | null => {
  if (!conversationId) return null;
  return state.messages.errorByConversation[conversationId] || null;
};

// Removed selectNextMessagesCursor as pagination is not handled here anymore

export default messagesSlice.reducer;
